import { useLocation, useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import { FaRupiahSign } from "react-icons/fa6";
import {
  getListPaymentConsultation,
  getListPaymentMethod,
} from "../../../services/purchaseApi";
import {
  verifyPurchase,
  verifyPurchaseConsultation,
} from "../../../services/transactionApi";
import { useNotification } from "../../../components/context/NotificationContext";

const formatRupiah = (number) => {
  return number ? Number(number).toLocaleString("id-ID") : 0;
};

const computePurchaseOrderTotal = (
  cart,
  discount,
  shippingPrice,
  paymentFee,
) => {
  const subtotal = (cart || []).reduce(
    (sum, item) => sum + item.quantity * item.price,
    0,
  );
  return (
    subtotal -
    Number(discount ?? 0) +
    Number(shippingPrice ?? 0) +
    Number(paymentFee ?? 0)
  );
};

const SkeletonLoader = () => {
  return (
    <div className="py-12 animate-pulse">
      <div className="flex items-center justify-center py-2 mb-2">
        <div className="flex flex-col items-center gap-2">
          <div className="w-14 h-14 bg-slate-200 rounded-full"></div>
          <div className="h-4 bg-slate-200 rounded w-24"></div>
          <div className="h-7 bg-slate-200 rounded w-40"></div>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="h-6 bg-slate-200 rounded w-40"></div>
          <div className="w-5 h-5 bg-slate-200 rounded"></div>
        </div>
      </div>

      <div className="mb-4">
        <div className="h-6 bg-slate-200 rounded w-44 mb-2"></div>
        <div className="flex flex-col gap-2">
          {[1, 2, 3, 4].map((item) => (
            <div
              key={item}
              className="w-full rounded-xl border border-slate-200 bg-slate-100 flex items-center justify-between px-3 py-2"
            >
              <div className="flex items-center gap-2 flex-1">
                <div className="w-10 h-10 bg-slate-200 rounded"></div>
                <div className="flex flex-col gap-2 flex-1">
                  <div className="h-4 bg-slate-200 rounded w-32"></div>
                  <div className="h-3 bg-slate-200 rounded w-28"></div>
                </div>
              </div>
              <div className="w-5 h-5 bg-slate-200 rounded-full"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PurchasePaymentMethodNew = () => {
  const machineId = localStorage.getItem("MachineId");
  const navigate = useNavigate();
  const location = useLocation();
  const { type, productType, checkoutData, cartApiData } = location.state || {};
  const { showNotification } = useNotification();

  const [expanded, setExpanded] = useState(true);
  const [paymentMethodExpand, setPaymentMethodExpand] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [limitedPaymentMethods, setLimitedPaymentMethods] = useState([]);
  const [allPaymentMethods, setAllPaymentMethods] = useState([]);

  const initialValues =
    type === 1
      ? {
          action_type: Number(productType),
          data: {
            payment_method_id: checkoutData?.data?.payment_method_id || null,
            shipping_method_id: checkoutData?.data?.shipping_method_id || null,
            shipping_method_name:
              checkoutData?.data?.shipping_method_name || "",
            shipping_price: checkoutData?.data?.shipping_price || 0,
            shipping_admin_fee: checkoutData?.data?.shipping_admin_fee || 0,
            note: checkoutData?.data?.note || "",
            fee: checkoutData?.data?.fee || 0,
            ppn: checkoutData?.data?.ppn || 0,
            total_transaction: computePurchaseOrderTotal(
              checkoutData?.cart,
              cartApiData?.result?.discount,
              checkoutData?.data?.shipping_price ??
                cartApiData?.result?.shipping ??
                0,
              checkoutData?.data?.fee || 0,
            ),
            uuid: machineId,
            shipment_code: checkoutData?.data?.shipment_code || "",
            shipment_type: checkoutData?.data?.shipment_type || "",
            shipping_provider: checkoutData?.data?.shipping_provider || "",
            shipping_service_type:
              checkoutData?.data?.shipping_service_type || "",
            shipping_service_name:
              checkoutData?.data?.shipping_service_name || "",
            shipping_group_code: checkoutData?.data?.shipping_group_code || "",
          },
          cart: checkoutData?.cart || [],
        }
      : {
          patient_type: checkoutData?.patient_type || 0,
          patient_name: checkoutData?.patient_name || "",
          nik: checkoutData?.nik || "",
          gender: checkoutData?.gender || 0,
          age: checkoutData?.age || 0,
          age_type: checkoutData?.age_type || 0,
          complaints: checkoutData?.complaints || "",
          images: checkoutData?.images || [],
          product_id: checkoutData?.product_id || 0,
          fee: 0,
          total_transaction: 0,
        };

  const formik = useFormik({
    initialValues,
    onSubmit: (value) => {
      if (type === 1) {
        mutateCheckoutVerify(value);
      } else {
        mutateCheckoutConsultation(value);
      }
    },
    validateOnMount: true,
  });

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const { data: paymentMethods, isFetching: isPaymentMethodsFetching } =
    useQuery({
      queryKey: ["list-payment-method"],
      queryFn: () =>
        type === 1 ? getListPaymentMethod() : getListPaymentConsultation(),
      refetchOnWindowFocus: false,
    });

  const { mutate: mutateCheckoutVerify, isPending: isVerifyLoading } =
    useMutation({
      mutationFn: verifyPurchase,
      onSuccess(invoice_number) {
        navigate(`/purchase/checkout/waiting-for-payment`, {
          state: {
            type,
            detail: invoice_number,
          },
        });
      },
      onError: (err) => {
        showNotification(
          err.response.data.message.id || "Terjadi kesalahan!",
          "error",
        );
      },
    });

  const {
    mutate: mutateCheckoutConsultation,
    isPending: isConsultationLoading,
  } = useMutation({
    mutationFn: verifyPurchaseConsultation,
    onSuccess(data) {
      navigate(`/purchase/checkout/waiting-for-payment`, {
        state: {
          type,
          detail: data,
        },
      });
    },
    onError: (err) => {
      showNotification(
        err.response.data.message.id || "Terjadi kesalahan!",
        "error",
      );
    },
  });

  useEffect(() => {
    if (paymentMethods) {
      if (type === 1) {
        setLimitedPaymentMethods(paymentMethods?.slice(0, 4) || []);
        setAllPaymentMethods(paymentMethods || []);
      } else if (type === 2) {
        formik.setFieldValue("total_transaction", paymentMethods?.doctor_fee);
        setLimitedPaymentMethods(
          paymentMethods?.payment_method?.slice(0, 4) || [],
        );
        setAllPaymentMethods(paymentMethods?.payment_method || []);
      } else {
        setLimitedPaymentMethods([]);
        setAllPaymentMethods([]);
      }
    }
  }, [paymentMethods, type]);

  const handleSelectPaymentMethod = (method, setFieldValue) => {
    setSelectedPaymentMethod(method);

    const fee = method.bank_fee || 0;
    const finalTotal =
      type === 1
        ? computePurchaseOrderTotal(
            formik.values.cart,
            cartApiData?.result?.discount,
            formik.values.data.shipping_price,
            fee,
          )
        : type === 2
          ? (paymentMethods?.doctor_fee || 0) + fee
          : 0;

    if (type === 1) {
      setFieldValue("data.payment_method_id", method.id);
      setFieldValue("data.fee", fee);
      setFieldValue("data.total_transaction", finalTotal);
    } else {
      setFieldValue("payment_id", method.id);
      setFieldValue("fee", fee);
      setFieldValue("total_transaction", finalTotal);
    }
  };

  const selectedPaymentMethodId =
    type === 1
      ? formik.values.data.payment_method_id
      : formik.values.payment_id;

  useEffect(() => {
    if (!selectedPaymentMethodId || !allPaymentMethods.length) return;

    const foundMethod = allPaymentMethods.find(
      (method) => method?.id === selectedPaymentMethodId,
    );

    if (foundMethod) {
      setSelectedPaymentMethod(foundMethod);
    }
  }, [selectedPaymentMethodId, allPaymentMethods]);

  const isPaymentMethodSelected = selectedPaymentMethod !== null;
  const isLoading = isPaymentMethodsFetching;

  return (
    <div className="w-full md:mt-4 min-h-screen">
      <div className="flex justify-center min-h-screen">
        <div className="w-full max-w-7xl mx-auto md:px-5 lg:px-0 text-base">
          <div className="hidden md:block mb-6 mt-4">
            <nav className="text-base text-slate-600 mb-3 flex flex-wrap items-center gap-1 font-medium">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="hover:text-slate-900 hover:underline transition-colors"
              >
                Beranda
              </button>
              <span className="text-slate-400">/</span>
              <button
                type="button"
                onClick={() => navigate("/purchase/cart-details")}
                className="hover:text-slate-900 hover:underline transition-colors"
              >
                Keranjang
              </button>
              <span className="text-slate-400">/</span>
              <button
                type="button"
                onClick={() => navigate("/purchase/checkout")}
                className="hover:text-slate-900 hover:underline transition-colors"
              >
                Checkout
              </button>
              <span className="text-slate-400">/</span>
              <span className="text-primary">Payment Method</span>
            </nav>

            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-semibold text-slate-800 tracking-tight">
                  Payment Method
                </h1>
                <p className="text-slate-500 mt-1 text-sm lg:text-base">
                  Pilih metode pembayaran yang kamu inginkan untuk menyelesaikan
                  transaksi pembelianmu.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-md font-medium text-slate-700 shadow-sm hover:bg-secondaryActive hover:border-secondary/30 hover:text-secondary transition-colors"
              >
                <IoArrowBack size={18} />
                Kembali
              </button>
            </div>
          </div>
          <div className="rounded-md py-4 px-3 lg:p-8 w-full h-fit">
            {isLoading ? (
              <SkeletonLoader />
            ) : (
              <form onSubmit={formik.handleSubmit}>
                <div className="w-full">
                  <div className="flex items-center justify-center py-2 mb-2">
                    <div className="flex flex-col items-center">
                      <div className="p-2 rounded-full bg-secondaryActive text-secondary">
                        <FaRupiahSign size={24} />
                      </div>
                      <p className="text-gray-500">Total Harga</p>
                      {type === 1 ? (
                        <p className="font-semibold text-xl text-gray-700 tabular-nums">
                          Rp
                          {formatRupiah(formik.values.data.total_transaction)}
                        </p>
                      ) : (
                        <p className="font-semibold text-xl text-gray-700 tabular-nums">
                          Rp{formatRupiah(formik.values.total_transaction)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div
                      onClick={toggleExpand}
                      className="flex items-center justify-between cursor-pointer"
                    >
                      <h1 className="font-semibold text-lg mb-1 text-slate-700">
                        Ringkasan Pesanan
                      </h1>
                      {expanded ? (
                        <IoChevronUpOutline
                          size={20}
                          className="text-gray-500"
                        />
                      ) : (
                        <IoChevronDownOutline
                          size={20}
                          className="text-gray-500"
                        />
                      )}
                    </div>
                    {expanded && (
                      <div className="w-full rounded-xl border border-gray-300 bg-white flex flex-col p-3">
                        <h1 className="font-semibold text-slate-700 mb-2">
                          {type === 1 ? "Daftar Obat" : "Konsultasi Dokter"}
                        </h1>
                        {type === 1 ? (
                          <div className="flex flex-col gap-1">
                            {formik.values.cart &&
                            formik.values.cart.length > 0 ? (
                              formik.values.cart.map((item, index) => (
                                <div
                                  key={index}
                                  className="flex items-end justify-between"
                                >
                                  <div className="flex flex-col">
                                    <p className="font-medium text-slate-700 line-clamp-1 leading-tight text-md">
                                      {item.product_name}
                                    </p>
                                    <p className="text-sm text-gray-500 tabular-nums">
                                      {item.quantity}x Rp
                                      {formatRupiah(item.price)}
                                    </p>
                                  </div>
                                  <p className="font-semibold text-slate-700 text-md tabular-nums">
                                    Rp
                                    {formatRupiah(item.quantity * item.price)}
                                  </p>
                                </div>
                              ))
                            ) : (
                              <p className="text-gray-500 text-center py-2">
                                Keranjang kosong
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-end justify-between">
                            <div className="flex flex-col">
                              <p className="font-medium text-slate-500 line-clamp-1 leading-tight text-md">
                                (Dokter Umum)
                              </p>
                            </div>
                          </div>
                        )}

                        <hr className="my-1" />
                        {type === 1 ? (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-slate-700 text-md">
                                Subtotal Produk
                              </p>
                              <p className="font-semibold text-slate-700 text-md tabular-nums">
                                Rp
                                {formatRupiah(
                                  formik.values.cart.reduce(
                                    (total, item) =>
                                      total + item.quantity * item.price,
                                    0,
                                  ),
                                )}
                              </p>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-slate-700 text-md">
                                Total Diskon
                              </p>
                              <p className="font-semibold text-slate-700 text-md tabular-nums">
                                Rp{formatRupiah(cartApiData?.result?.discount)}
                              </p>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-slate-700 text-md">
                                Biaya Pengiriman
                              </p>
                              <p className="font-semibold text-slate-700 text-md tabular-nums">
                                Rp
                                {formatRupiah(
                                  formik.values.data.shipping_price,
                                )}
                              </p>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-slate-700 text-md">
                                Biaya Layanan
                              </p>
                              <p className="font-semibold text-slate-700 text-md tabular-nums">
                                Rp{formatRupiah(formik.values.data.fee)}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-slate-500 text-md">
                                Jasa Dokter
                              </p>
                              <p className="font-semibold text-slate-700 text-md tabular-nums">
                                Rp
                                {formatRupiah(
                                  paymentMethods && paymentMethods.doctor_fee,
                                )}
                              </p>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-slate-500 text-md">
                                Biaya Layanan
                              </p>
                              <p className="font-semibold text-slate-700 text-md tabular-nums">
                                Rp{formatRupiah(formik.values.fee)}
                              </p>
                            </div>
                          </div>
                        )}

                        <hr className="my-1" />

                        <div className="flex items-center justify-between">
                          <h1 className="font-semibold text-slate-700">
                            Total
                          </h1>
                          <p className="font-semibold text-error tabular-nums">
                            Rp
                            {formatRupiah(
                              type === 1
                                ? formik.values.data.total_transaction
                                : formik.values.total_transaction,
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <h1 className="font-semibold text-lg mb-1 text-slate-700">
                      Metode Pembayaran
                    </h1>
                    <div className="flex flex-col gap-2">
                      {(paymentMethodExpand
                        ? allPaymentMethods
                        : limitedPaymentMethods
                      )?.map((item, index) => (
                        <div
                          key={index}
                          className={`w-full rounded-xl border ${
                            selectedPaymentMethod?.id === item?.id
                              ? "border-secondary bg-secondaryActive"
                              : "border-slate-300 bg-slate-100"
                          } flex items-center justify-between px-3 py-1 cursor-pointer transition-colors duration-200`}
                          onClick={() =>
                            handleSelectPaymentMethod(
                              item,
                              formik.setFieldValue,
                            )
                          }
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 flex items-center justify-center">
                              {item.logo ? (
                                <img
                                  src={item.logo}
                                  alt={item.pg_bank}
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                                  <span className="text-xs font-medium">
                                    LOGO
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col">
                              <h1 className="font-semibold text-slate-700 text-md">
                                {item.pg_bank}
                              </h1>
                              <p className="text-slate-500 text-sm tabular-nums">
                                Biaya admin: Rp
                                {formatRupiah(item.bank_fee || 0)}
                              </p>
                            </div>
                          </div>
                          <input
                            type="radio"
                            name="payment-method"
                            checked={selectedPaymentMethod?.id === item.id}
                            onChange={() =>
                              handleSelectPaymentMethod(
                                item,
                                formik.setFieldValue,
                              )
                            }
                            className={`w-5 h-5 text-secondary focus:ring-secondary cursor-pointer`}
                          />
                        </div>
                      ))}

                      {allPaymentMethods.length > 4 && (
                        <div className="flex w-full">
                          <button
                            type="button"
                            onClick={() =>
                              setPaymentMethodExpand(!paymentMethodExpand)
                            }
                            className="w-full flex gap-1 items-center justify-center text-primary py-2 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                          >
                            {paymentMethodExpand ? (
                              <>
                                <p className="font-semibold text-md">
                                  Lebih Sedikit
                                </p>
                                <IoChevronUpOutline size={16} />
                              </>
                            ) : (
                              <>
                                <p className="font-semibold text-md">
                                  Lihat Semua
                                </p>
                                <IoChevronDownOutline size={16} />
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="text-base fixed md:hidden bottom-0 left-0 right-0 bg-white rounded-t-3xl py-4 px-3 z-[99] max-h-[70vh] flex flex-col shadow-2xl">
        <button
          type="submit"
          disabled={
            !isPaymentMethodSelected ||
            isVerifyLoading ||
            isConsultationLoading ||
            isLoading
          }
          onClick={formik.handleSubmit}
          className="h-[40px] border text-white flex items-center gap-2 justify-center w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline transform duration-200 hover:scale-100 border-secondary bg-secondary disabled:border-slate-300 disabled:bg-slate-300 disabled:cursor-default"
        >
          {type === 1 && !isVerifyLoading ? (
            <span className="tabular-nums">
              {isPaymentMethodSelected
                ? `Bayar Sekarang Rp${formatRupiah(formik.values.data.total_transaction)}`
                : "Pilih metode pembayaran"}
            </span>
          ) : type === 2 && !isConsultationLoading ? (
            <span className="tabular-nums">
              {isPaymentMethodSelected
                ? `Bayar sekarang Rp${formatRupiah(formik.values.total_transaction)}`
                : "Pilih metode pembayaran"}
            </span>
          ) : isVerifyLoading || isConsultationLoading ? (
            <p className="animate-pulse">Memproses...</p>
          ) : null}
        </button>
      </div>
    </div>
  );
};

export default PurchasePaymentMethodNew;
